import { Component } from '@theme/component';
import { lockScroll, unlockScroll } from '@theme/utilities';

/**
 * A custom element that renders a horizontally scrollable video carousel.
 * Clicking a thumbnail opens the matching video full-screen in a modal player.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} track - The scrollable track holding the video items.
 * @property {HTMLButtonElement} [prev] - The previous arrow button.
 * @property {HTMLButtonElement} [next] - The next arrow button.
 * @property {HTMLDialogElement} dialog - The modal dialog element.
 * @property {HTMLVideoElement} modalVideo - The video element rendered inside the modal.
 * @property {HTMLButtonElement} closeButton - The button that closes the modal.
 *
 * @extends Component<Refs>
 */
class VideoCarousel extends Component {
  requiredRefs = ['track', 'dialog', 'modalVideo', 'closeButton'];

  connectedCallback() {
    super.connectedCallback();

    this.refs.track.addEventListener('click', this.#handleTrackClick);
    this.refs.track.addEventListener('scroll', this.#updateArrowVisibility, { passive: true });
    this.refs.prev?.addEventListener('click', () => this.#scrollByAmount(-1));
    this.refs.next?.addEventListener('click', () => this.#scrollByAmount(1));

    this.refs.closeButton.addEventListener('click', this.closeModal);
    this.refs.dialog.addEventListener('click', this.#handleDialogClick);
    this.refs.dialog.addEventListener('close', this.#handleDialogClose);
    this.refs.dialog.addEventListener('cancel', this.#handleDialogClose);

    window.addEventListener('resize', this.#updateArrowVisibility);
    requestAnimationFrame(this.#updateArrowVisibility);

    this.#posterObserver = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.#loadPosters();
        this.#posterObserver?.disconnect();
      },
      { rootMargin: '300px 0px' }
    );
    this.#posterObserver.observe(this);
  }

  /** @type {IntersectionObserver | undefined} */
  #posterObserver;

  /**
   * Posters render with src/srcset parked in data-* (see video-carousel.liquid)
   * so they stay out of the product page's LCP window; restore them once the
   * carousel is close to the viewport.
   */
  #loadPosters() {
    for (const img of this.querySelectorAll('img[data-src]')) {
      if (!(img instanceof HTMLImageElement)) continue;
      if (img.dataset.srcset) img.srcset = img.dataset.srcset;
      img.src = img.dataset.src || '';
      img.removeAttribute('data-srcset');
      img.removeAttribute('data-src');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#posterObserver?.disconnect();

    this.refs.track.removeEventListener('click', this.#handleTrackClick);
    this.refs.track.removeEventListener('scroll', this.#updateArrowVisibility);
    this.refs.closeButton.removeEventListener('click', this.closeModal);
    this.refs.dialog.removeEventListener('click', this.#handleDialogClick);
    this.refs.dialog.removeEventListener('close', this.#handleDialogClose);
    this.refs.dialog.removeEventListener('cancel', this.#handleDialogClose);
    window.removeEventListener('resize', this.#updateArrowVisibility);
  }

  /**
   * Hides the prev/next arrow once the track can't scroll any further in
   * that direction, instead of always showing both.
   */
  #updateArrowVisibility = () => {
    const { track, prev, next } = this.refs;
    const maxScroll = track.scrollWidth - track.clientWidth;
    const tolerance = 1;

    prev?.toggleAttribute('hidden', track.scrollLeft <= tolerance);
    next?.toggleAttribute('hidden', track.scrollLeft >= maxScroll - tolerance);
  };

  /**
   * @param {MouseEvent} event
   */
  #handleTrackClick = (event) => {
    // Only the thumbnail opens the player; the kit-name link below it should navigate.
    const thumb = event.target instanceof Element ? event.target.closest('.video-carousel__thumb') : null;
    const target = thumb?.closest('[data-video-item]');
    if (!(target instanceof HTMLElement)) return;

    const template = target.querySelector('template[data-video-sources]');
    if (!(template instanceof HTMLTemplateElement)) return;

    this.openModal(template, target.getAttribute('data-poster') || '');
  };

  /**
   * Opens the modal player with the sources cloned from the given template.
   * @param {HTMLTemplateElement} sourcesTemplate
   * @param {string} poster
   */
  openModal(sourcesTemplate, poster) {
    const { dialog, modalVideo } = this.refs;

    // Set the poster first: it gives the video a painted frame from the very first
    // layout pass, instead of a black box that only fills in once metadata arrives.
    if (poster) modalVideo.setAttribute('poster', poster);
    modalVideo.replaceChildren(sourcesTemplate.content.cloneNode(true));
    modalVideo.load();

    lockScroll(dialog);
    dialog.showModal();

    modalVideo.play().catch(() => {
      // Autoplay can be blocked by the browser; the user can press play manually.
    });
  }

  closeModal = () => {
    const { dialog } = this.refs;
    if (dialog.open) dialog.close();
  };

  /**
   * @param {MouseEvent} event
   */
  #handleDialogClick = (event) => {
    const { dialog } = this.refs;
    if (event.target === dialog) this.closeModal();
  };

  #handleDialogClose = () => {
    const { modalVideo, dialog } = this.refs;
    modalVideo.pause();
    modalVideo.replaceChildren();
    modalVideo.removeAttribute('poster');
    modalVideo.load();
    unlockScroll(dialog);
  };

  /**
   * @param {number} direction - -1 for previous, 1 for next.
   */
  #scrollByAmount(direction) {
    const { track } = this.refs;
    const item = track.querySelector('[data-video-item]');
    const itemWidth = item instanceof HTMLElement ? item.getBoundingClientRect().width : track.clientWidth * 0.8;
    const gap = parseFloat(getComputedStyle(track).columnGap || '0');

    track.scrollBy({
      left: direction * (itemWidth + gap),
      behavior: 'smooth',
    });
  }
}

if (!customElements.get('video-carousel')) {
  customElements.define('video-carousel', VideoCarousel);
}
